/**
* @summary - Component that Provides Change Password functionality
* @autor vengateshwaran k
* @since 2024-05-30
*/
import { yupResolver } from "@hookform/resolvers/yup";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box, Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField, Typography
} from "@mui/material";
import CryptoJS from "crypto-js";
import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import UserRepository from '../../../src/api/repositories/userRepository';
import { BreadcrumbBar } from "../../assets/styled-components/breadcrumbBar";
import { DefaultCard } from "../../assets/styled-components/card";
import CustomDialog from "../../components/commoncomponents/CustomDialog";
import PageTitle from "../../components/commoncomponents/PageTitle";
import { setHeaderName, setSelectedIndex } from "../../redux/actions/userActions";
import { encryptionKey } from "../../utility/secretKeys";
import StyledBanner from "../../components/commoncomponents/StyledBanner";
import WelcomeCard from "../../components/commoncomponents/WelcomeCard";

const ExpiredPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [content, setContent] = useState('');
  const [unsavedAddDialogOpen, setUnsavedAddDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formDirty, setFormDirty] = useState(false); // Track if the form has been modified
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // State to toggle confirm password visibility
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [oldPasswordIncorrect, setOldPasswordIncorrect] = useState(false);
  const [isPasswordSame, setIsPasswordSame] = useState(false);
  const [isPasswordChanged, setIsPasswordChanged] = useState(false);

  // State variables to store password values
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const oldPasswordRef = useRef();
  const schema = yup.object().shape({
    oldPassword: yup.string().required('Old Password is required'),
    newPassword: yup.string().required('New Password is required').min(8, 'Password must be at least 8 characters').max(14, 'Password must be at most 14 characters')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]+$/, 'Password must be alphanumeric with special characters'),
    confirmPassword: yup.string().oneOf([yup.ref('newPassword'), null], 'Confirm Password must match new password')
  });

  const { register, handleSubmit, formState: { errors }, reset, watch, clearErrors, trigger } = useForm({
    resolver: yupResolver(schema)
  });
  // Update the onFocus handler for the password fields
  const handlePasswordFocus = (field) => () => {
    if (errors[field]) {
      clearErrors(field);
    }
  };
  // Watch form fields to determine if they are filled
  const formValues = watch();

  const encrypt = (key, content) => {
    let keyBytes = CryptoJS.enc.Utf8.parse(key);
    let keyWords = keyBytes.words.slice(0, 8);  // AES-256 uses 8 words (32 bytes)
    let truncatedKey = CryptoJS.lib.WordArray.create(keyWords, 32);
    let iv = CryptoJS.lib.WordArray.random(16);
    let encrypted = CryptoJS.AES.encrypt(content, truncatedKey, { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 });
    let encryptedData = iv.concat(encrypted.ciphertext);
    return CryptoJS.enc.Base64.stringify(encryptedData);
  };

  const onSubmit = async (data) => {
    try {
      const isValid = await trigger("newPassword"); // ✅ Manually trigger validation before submission
      if (!isValid) return; // ✅ Stop execution if validation fails

      if (data.oldPassword !== data.newPassword) {
        const values = { currentPassword: data.oldPassword, newPassword: data.newPassword };
        const response = await UserRepository.changePassword(values);

        if (response.status === 200) {
          setIsPasswordChanged(true);
          setOldPasswordIncorrect(false);
          setIsPasswordSame(false);
          setErrorDialogOpen(true);
        } else if (response?.status === 400) {
          setOldPasswordIncorrect(true);
          setErrorDialogOpen(true);
        }
      } else {
        setIsPasswordSame(true);
        setErrorDialogOpen(true);
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please try again later.");
    }
  };

  useEffect(() => {
    if (errors.oldPassword) {
      oldPasswordRef.current.blur();
    }
  }, [errors])
  const handleCancelClick = () => {
    // Check if any field is empty or if the form has been modified
    const isFormEmpty = Object.values(formValues).some(value => !value);

    // If form is empty OR the password fields are empty in both the form and the state
    if (!isFormEmpty ||
      (oldPassword === '' && newPassword === '' && confirmPassword === '') &&
      (formValues?.oldPassword === '' && formValues?.newPassword === '' && formValues?.confirmPassword === '')) {

      // Redirect directly to dashboard if form is empty
      if (localStorage.getItem("roleName") === "DeptUser") {
        navigate("/login");
      } else {
        navigate("/login");
      }

      // Dispatch actions to reset selected index and header name
      dispatch(setSelectedIndex('SET_INDEX_LABEL', ''));
      dispatch(setHeaderName('SET_HEADER_NAME', 'Dashboard'));
    } else {
      // Show unsaved changes dialog if form has been modified and not empty
      setUnsavedAddDialogOpen(true);
    }
  };

  const handleAddNewClick = () => {
    reset({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setOldPassword('');
    setConfirmPassword('');
    setNewPassword('');
    setErrorMessage("");
    setContent('');
  };
  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  return (
    <>
    <StyledBanner>

      
      <Box mt={{ xs: 1, md: 2 }}>
      
        <DefaultCard sx={{ height: "100%",width: "70%",alignContent: "center",justifyContent: "center",margin: "auto",padding: "20px" }}>
        <h2> Change Password</h2>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', marginTop: '1rem' }}>
            <Grid container sx={{ display: "flex", justifyContent: "center" }}>
              <Grid container>
                <Grid item xs={12} sm={4} style={{ marginTop: '20px' }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Typography variant='h6' color={'text.primary'} component={'label'}>Old Password<Typography component={"span"} color={"error"}>
                    *
                  </Typography></Typography>
                </Grid>
                <Grid item xs={12} sm={2.5} sx={{ marginLeft: '20px' }}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="oldPassword"
                    inputRef={oldPasswordRef}
                    placeholder="Enter Old Password"
                    type="password"
                    name="oldPassword"
                    {...register("oldPassword")}
                    error={!!errors.oldPassword}
                    helperText={errors.oldPassword ? errors.oldPassword.message : ''}
                    onChange={(event) => {
                      clearErrors("oldPassword"); setFormDirty(true);
                      setOldPassword(event.target.value)
                    }}
                  //onFocus={handlePasswordFocus("oldPassword")} // Updated handler
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} sm={4} style={{ marginTop: '20px' }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Typography variant='h6' color={'text.primary'} component={'label'}>New Password<Typography component={"span"} color={"error"}>
                    *
                  </Typography></Typography>
                </Grid>
                <Grid item xs={12} sm={2.5} sx={{ marginLeft: '20px' }}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="newPassword"
                    placeholder="Enter New Password"
                    inputProps={{ minLength: 8, maxLength: 14 }}
                    type="password"
                    name="newPassword"
                    autoComplete="new-password"
                    {...register("newPassword", { required: "New Password is required" })} // ✅ Ensure validation
                    error={!!errors.newPassword}
                    helperText={errors.newPassword?.message || ""}
                  //  onFocus={() => trigger("newPassword")} // ✅ Ensure validation is triggered on focus
                  />

                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} sm={4} style={{ marginTop: '20px' }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Typography variant='h6' color={'text.primary'} component={'label'}>Confirm Password<Typography component={"span"} color={"error"}>
                    *
                  </Typography></Typography>
                </Grid>
                <Grid item xs={12} sm={2.5} sx={{ marginLeft: '20px' }}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="confirmPassword"
                    placeholder="Enter Confirm Password"
                    inputProps={{ minLength: 8, maxLength: 14 }}
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    autoComplete="new-password"
                    {...register("confirmPassword")}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword ? errors.confirmPassword.message : ''}
                    onChange={(event) => {
                      setFormDirty(true);
                      setConfirmPassword(event.target.value)

                    }}
                    onFocus={handlePasswordFocus("confirmPassword")} // Updated handler
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={handleClickShowConfirmPassword}
                            edge="end"
                          >
                            {!showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={6} sm={12}>
                  <Typography sx={{ margin: 2 }}>
                    1. Password should be a mix of alphabets with at least one uppercase letter, numerals, and special characters (@$!%*?&#^) without any spaces.
                  </Typography>
                  <Typography sx={{ margin: 2 }}>
                    2. The Password length should be between 8 to 14 characters.
                  </Typography>
                  <Typography sx={{ margin: 2 }}>
                    3. New Password cannot be the same as the previous password.
                  </Typography>
                </Grid>
              </Grid>
              <Grid container sx={{ display: "flex", justifyContent: "flex-end", margin: '20px' }}>
                <Grid item xs={12} sm={3}>
                  {errorMessage && (
                    <Alert severity="error" style={{ marginTop: '1rem' }}>{errorMessage}</Alert>
                  )}
                  {successMessage ? (
                    <Alert severity="success" style={{ margin: '1rem' }}>{successMessage}</Alert>
                  ) : (
                    <>
                      <Button type="submit" variant="contained" style={{ margin: '1rem' }}>
                        Save
                      </Button>
                      <Button variant='contained' onClick={handleAddNewClick} sx={{ margin: "10px" }}>CLEAR</Button>
                      <Button variant='outlined' onClick={handleCancelClick} sx={{ margin: "10px" }}>Back</Button>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </form>
        </DefaultCard>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: { xs: "center", md: "end" },
          }}
        ></Box>
      </Box>

      <CustomDialog
        open={unsavedAddDialogOpen}
        content="Record(s) not Saved/Updated. Do you want to continue?"
        onConfirm={() => {
          setUnsavedAddDialogOpen(false); navigate('/login');
          dispatch(setSelectedIndex('SET_INDEX_LABEL', ''));
          dispatch(setHeaderName('SET_HEADER_NAME', 'Dashboard'));
        }}
        onClose={() => { setUnsavedAddDialogOpen(false) }}
        cancelBtn="No"
        confirmBtn="Yes"
      />
      <CustomDialog
        open={errorDialogOpen}
        content={oldPasswordIncorrect ? "You have entered Incorrect Old Password" : isPasswordSame ? "Unable to change password because Old Password and New Password are same." : "Password changed successfully. Redirecting to login..."}
        onClose={() => setErrorDialogOpen(false)}
        onConfirm={() => {
          setErrorDialogOpen(false);
          if (isPasswordChanged) {
            setTimeout(() => {
              navigate('/login');
              localStorage.setItem('rememberMe', 'false');
            }, 2000);
          }
        }}
        confirmBtn="Ok"
      />
    </StyledBanner>
    </>
  );
};

export default ExpiredPassword;
